// TeamList.js
import React, { useState, useEffect } from "react";
import { Edit, Trash2, UserPlus, RefreshCw, Clock, Mail, X } from "lucide-react";
import TeamService from "../services/teamService";
import CreateTeamMemberForm from "./CreateTeamMemberForm";
import EditTeamMemberForm from "./EditTeamMemberForm";

const TeamList = ({ onEdit, onDelete }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersResponse, invitesResponse] = await Promise.all([
        TeamService.getAllTeamMembers(),
        TeamService.getInvites(),
      ]);
      setTeamMembers(membersResponse.data);
      setInvites(invitesResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm("Are you sure you want to delete this team member?")) return;

    try {
      await TeamService.deleteTeamMember(memberId);
      setTeamMembers(teamMembers.filter((member) => member.id !== memberId));
    } catch (error) {
      console.error("Error deleting team member:", error);
      alert("Failed to delete team member. Please try again.");
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      setResendingId(inviteId);
      const response = await TeamService.resendInvite(inviteId);
      
      // Update the invite in state
      setInvites(invites.map((invite) =>
        invite.id === inviteId ? response.data : invite
      ));
      
      alert("Invite resent successfully!");
    } catch (error) {
      console.error("Error resending invite:", error);
      alert("Failed to resend invite. Please try again.");
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm("Are you sure you want to cancel this invite?")) return;

    try {
      await TeamService.deleteInvite(inviteId);
      setInvites(invites.filter((invite) => invite.id !== inviteId));
    } catch (error) {
      console.error("Error cancelling invite:", error);
      alert("Failed to cancel invite. Please try again.");
    }
  };

  const handleCreateSuccess = (newMember) => {
    // Check if it's an invite or a direct member creation
    if (newMember.status === 'pending' || newMember.expires_at) {
      // It's an invite
      setInvites([...invites, newMember]);
    } else {
      setTeamMembers([...teamMembers, newMember]);
    }
  };

  const handleEditSuccess = (updatedMember) => {
    setTeamMembers(
      teamMembers.map((member) =>
        member.id === updatedMember.id ? updatedMember : member
      )
    );
    setEditingMember(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    return `${hours}h remaining`;
  };

  if (loading) {
    return <div className="p-6">Loading team members...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Team Members Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Member
          </button>
        </div>

        {showCreateForm && (
          <CreateTeamMemberForm
            onClose={() => setShowCreateForm(false)}
            onSuccess={handleCreateSuccess}
          />
        )}
        {editingMember && (
          <EditTeamMemberForm
            member={editingMember}
            onClose={() => setEditingMember(null)}
            onSuccess={handleEditSuccess}
          />
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    No team members yet
                  </td>
                </tr>
              ) : (
                teamMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {member.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => setEditingMember(member)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(member.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invites Section */}
      {invites.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Invites
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({invites.length})
              </span>
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invites.map((invite) => (
                  <tr key={invite.id} className={invite.is_expired ? "bg-red-50" : ""}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                        {invite.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invite.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {invite.is_expired ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Clock className="h-3 w-3 mr-1" />
                          Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="h-3 w-3 mr-1" />
                          {getTimeRemaining(invite.expires_at) || "Pending"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(invite.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => handleResendInvite(invite.id)}
                        disabled={resendingId === invite.id}
                        className={`inline-flex items-center mr-3 ${
                          invite.is_expired
                            ? "text-green-600 hover:text-green-900"
                            : "text-blue-600 hover:text-blue-900"
                        } disabled:opacity-50`}
                        title={invite.is_expired ? "Resend Invite" : "Resend"}
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${
                            resendingId === invite.id ? "animate-spin" : ""
                          }`}
                        />
                        <span className="ml-1 text-xs">
                          {invite.is_expired ? "Resend" : "Resend"}
                        </span>
                      </button>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        className="inline-flex items-center text-red-600 hover:text-red-900"
                        title="Cancel Invite"
                      >
                        <X className="h-4 w-4" />
                        <span className="ml-1 text-xs">Cancel</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamList;
